import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import {
  Search, Calendar, Clock, Users, Zap, CheckCircle2,
  Truck, MapPinned, Inbox, Loader2, PlayCircle
} from "lucide-react";

import RouteRow from "../Includes/RouteRow";

const TABS = [
  { key: "offers",    label: "Job Offers" },
  { key: "upcoming",  label: "Upcoming" },
  { key: "active",    label: "Active" },
  { key: "completed", label: "Completed" },
];

const STATUS_STYLES = {
  offer:     { label: "Awaiting Response", bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  upcoming:  { label: "Assigned",          bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200" },
  transit:   { label: "In Transit",        bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  delivered: { label: "Completed",         bg: "bg-slate-100",  text: "text-slate-600",   border: "border-slate-200" },
};

function scheduleBucket(dateStr) {
  if (!dateStr) return "Date TBD";
  const d = new Date(dateStr);
  if (isNaN(d)) return "Date TBD";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays < 0) return "Past";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays <= 7) return "This Week";
  if (diffDays <= 30) return "This Month";
  return "Later";
}

function JobCard({ job, statusKey, isApplication }) {
  const navigate = useNavigate();
  const style = STATUS_STYLES[statusKey] || STATUS_STYLES.upcoming;
  
  const loadData = job.load || {};
  const displayId = loadData.id || loadData.reference_code || job.id;

  const handleActionClick = () => {
    if (isApplication) {
      navigate(`/load-board`); 
    } else if (statusKey === "upcoming") {
      // 🚨 EXPLICIT GPS PERMISSION CHECK 🚨
      if (!navigator.geolocation) {
        alert("GPS is not supported by your browser or device.");
        return;
      }

      // This triggers the native mobile/browser prompt asking for location access.
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Success: User clicked ALLOW. Safe to navigate to the console.
          navigate(`/my-jobs/console/${job.id}`);
        },
        (error) => {
          // Error: User clicked DENY, or location services are turned off in phone settings.
          if (error.code === error.PERMISSION_DENIED) {
            alert("🛑 Location Access Denied! You MUST allow GPS tracking to start this load. Please enable location in your device settings.");
          } else {
            alert("⚠️ Unable to secure a GPS signal. Please ensure your location services are turned on.");
          }
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    } else {
      // If already in transit or completed, navigate normally.
      navigate(`/my-jobs/console/${job.id}`);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4 hover:border-teal-300 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">#{displayId}</span>
          <span className={`text-[10px] font-black px-2 py-1 rounded-md border flex items-center gap-1 ${style.bg} ${style.text} ${style.border}`}>
            {statusKey === "transit" && <Truck className="w-3 h-3" />}
            {statusKey === "offer" && <Zap className="w-3 h-3" />}
            {statusKey === "delivered" && <CheckCircle2 className="w-3 h-3" />}
            {style.label}
          </span>
        </div>
        <span className="text-xs font-bold text-gray-400 flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" /> {loadData.date || "TBD"}
        </span>
      </div>

      <h3 className="font-black text-gray-900 text-base -mt-2">
        {loadData.type || loadData.commodity || "Oversize Load"}
      </h3>

      <RouteRow 
        from={loadData.from || "Location Hidden (Review Offer)"} 
        to={loadData.to || "Location Hidden"} 
        compact 
      />

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Payout</p>
          <p className="text-lg font-black text-emerald-600">{loadData.pay || "—"}</p>
        </div>
        <div className="text-right">
          <p className="text-xs font-bold text-gray-500 flex items-center gap-1 justify-end">
            <Users className="w-3 h-3" /> {loadData.escorts || 1} escort
          </p>
        </div>
      </div>

      <button 
        onClick={handleActionClick}
        className={`w-full mt-1 border font-bold py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-1.5 ${
          statusKey === "upcoming" 
            ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 shadow-md shadow-emerald-600/20" 
            : statusKey === "transit"
            ? "bg-[#1a2744] hover:bg-blue-900 text-white border-[#1a2744]"
            : "bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700"
        }`}
      >
        {statusKey === "offer" ? (
          <><MapPinned className="w-4 h-4" /> Review Offer</>
        ) : statusKey === "upcoming" ? (
          <><PlayCircle className="w-4 h-4" /> Start Load & Enable GPS</>
        ) : statusKey === "transit" ? (
          <><Truck className="w-4 h-4" /> Open Live Console</>
        ) : (
          <><CheckCircle2 className="w-4 h-4" /> View Details</>
        )}
      </button>
    </div>
  );
}

export default function MyJobs() {
  const navigate = useNavigate();
  
  // 1. Grab user directly from the Layout Context (No more local storage checks here!)
  const { user } = useOutletContext() || {};

  const [tab, setTab] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState({ offers: [], upcoming: [], active: [], completed: [] });

  // 2. Fetch Jobs from Backend
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("user_token") || localStorage.getItem("access_token");
        if (!token) return;

        const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

        // A. Fetch Active/Completed Assignments (The actual jobs)
        const assignRes = await fetch(`${apiUrl}/api/loads/assignments/mine/`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const assignments = assignRes.ok ? await assignRes.json() : [];

        // B. Fetch Pending Applications/Offers
        const appRes = await fetch(`${apiUrl}/api/loads/applications/mine/`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const applications = appRes.ok ? await appRes.json() : [];

        // Organize into buckets
        const offers = applications.filter(a => a.status === "invited" || a.status === "pending");
        const upcoming = assignments.filter(a => a.status === "assigned" || a.status === "paused");
        const active = assignments.filter(a => a.status === "started" || a.status === "in_transit");
        const completed = assignments.filter(a => a.status === "completed" || a.status === "cancelled");

        setJobs({ offers, upcoming, active, completed });
        
        // Auto-switch to active tab if driver has an active job
        if (active.length > 0 && tab === "upcoming") {
          setTab("active");
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const list = jobs[tab] || [];

  const grouped = tab === "upcoming"
    ? list.reduce((acc, job) => {
        const bucket = scheduleBucket(job.load?.date);
        acc[bucket] = acc[bucket] || [];
        acc[bucket].push(job);
        return acc;
      }, {})
    : null;

  const bucketOrder = ["Today", "Tomorrow", "This Week", "This Month", "Later", "Date TBD", "Past"];

  return (
    <div style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      
      {/* 
        Header + Tabs Area
        Negative margins pull this container flush against the layout edges, mimicking the Load Board alignment.
      */}
      <div className="-mt-6 sm:-mt-8 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-5 bg-white border-b border-gray-200 mb-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-gray-900">My Jobs</h1>
            <p className="text-sm text-gray-500 mt-1">Manage job offers, your schedule, and active runs.</p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex bg-slate-100 rounded-xl p-1 border border-gray-200 overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap flex items-center gap-2 ${
                    tab === t.key ? "bg-[#1a2744] text-white shadow" : "text-gray-500 hover:text-gray-800 hover:bg-white"
                  }`}
                >
                  {t.label}
                  {jobs[t.key]?.length > 0 && (
                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${tab === t.key ? "bg-white/20 text-white" : "bg-gray-200 text-gray-600"}`}>
                      {jobs[t.key].length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <button 
              onClick={() => navigate("/load-board")}
              className="bg-[#1a2744] hover:bg-blue-900 text-white font-bold px-4 py-2 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm shadow-sm hidden sm:flex"
            >
              <Search className="w-4 h-4" /> Find Loads
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-5xl mx-auto pb-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-white rounded-2xl border border-gray-200">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600 mb-3" />
            <span className="text-sm font-semibold">Loading your jobs...</span>
          </div>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center border-2 border-dashed border-gray-200 rounded-2xl bg-white">
            <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4 border border-slate-100">
              <Inbox className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">Nothing here yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mb-6 leading-relaxed">
              {tab === "offers" && "When a company sends you a direct job offer, it'll show up here for you to review."}
              {tab === "upcoming" && "Accepted jobs that haven't started yet will appear here."}
              {tab === "active" && "Loads currently in transit will show up here with live progress."}
              {tab === "completed" && "Finished loads and their payouts will be archived here."}
            </p>
            <button 
              onClick={() => navigate("/load-board")}
              className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 font-bold px-5 py-2.5 rounded-xl transition-colors text-sm shadow-sm"
            >
              Browse the Load Board
            </button>
          </div>
        ) : tab === "upcoming" ? (
          <div className="space-y-6">
            {bucketOrder.filter((b) => grouped[b]?.length).map((bucket) => (
              <div key={bucket}>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <h2 className="text-sm font-black text-gray-700 uppercase tracking-wider">{bucket}</h2>
                  <span className="text-xs font-bold text-gray-400">({grouped[bucket].length})</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {grouped[bucket].map((job) => (
                    <JobCard key={job.id} job={job} statusKey="upcoming" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {list.map((job) => (
              <JobCard 
                key={job.id} 
                job={job} 
                isApplication={tab === "offers"} 
                statusKey={tab === "offers" ? "offer" : tab === "active" ? "transit" : "delivered"} 
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}